export const reclaimPartnerCodeIntakeStatuses = [
  "applied",
  "skipped_free_terms",
  "skipped_without_code",
  "unavailable",
] as const;

export type ReclaimPartnerCodeIntakeStatus =
  (typeof reclaimPartnerCodeIntakeStatuses)[number];

export const RECLAIM_PARTNER_CODE_STATUS_ANSWER_ID = "reclaimPartnerCodeStatus";
export const RECLAIM_PARTNER_CODE_ANSWER_ID = "reclaimPartnerCode";
export const RECLAIM_PARTNER_CODE_PARTNER_NAME_ANSWER_ID =
  "reclaimPartnerCodePartnerName";
export const RECLAIM_PARTNER_CODE_PARTNER_PROFILE_IMAGE_URL_ANSWER_ID =
  "reclaimPartnerCodePartnerProfileImageUrl";

export interface ReclaimPartnerCodeIntakeState {
  partnerName?: string;
  partnerProfileImageUrl?: string;
  referralCode?: string;
  status?: ReclaimPartnerCodeIntakeStatus;
}

export function buildReclaimPartnerCodeIntakeAnswers({
  partnerName,
  partnerProfileImageUrl,
  referralCode,
  status,
}: ReclaimPartnerCodeIntakeState & {
  status: ReclaimPartnerCodeIntakeStatus;
}): Record<string, string> {
  const normalizedReferralCode = referralCode?.trim().toUpperCase() ?? "";
  const normalizedPartnerName = partnerName?.trim() ?? "";
  const normalizedPartnerProfileImageUrl = partnerProfileImageUrl?.trim() ?? "";
  return {
    [RECLAIM_PARTNER_CODE_STATUS_ANSWER_ID]: status,
    [RECLAIM_PARTNER_CODE_ANSWER_ID]: normalizedReferralCode,
    [RECLAIM_PARTNER_CODE_PARTNER_NAME_ANSWER_ID]: normalizedPartnerName,
    [RECLAIM_PARTNER_CODE_PARTNER_PROFILE_IMAGE_URL_ANSWER_ID]:
      normalizedPartnerProfileImageUrl,
  };
}

export function getReclaimPartnerCodeIntakeState(
  answers: Record<string, unknown> | null | undefined,
): ReclaimPartnerCodeIntakeState {
  const status = parseStatus(answers?.[RECLAIM_PARTNER_CODE_STATUS_ANSWER_ID]);
  const referralCode = parseString(answers?.[RECLAIM_PARTNER_CODE_ANSWER_ID]);
  const partnerName = parseString(
    answers?.[RECLAIM_PARTNER_CODE_PARTNER_NAME_ANSWER_ID],
  );
  const partnerProfileImageUrl = parseString(
    answers?.[RECLAIM_PARTNER_CODE_PARTNER_PROFILE_IMAGE_URL_ANSWER_ID],
  );
  return {
    ...(status ? { status } : {}),
    ...(referralCode ? { referralCode } : {}),
    ...(partnerName ? { partnerName } : {}),
    ...(partnerProfileImageUrl ? { partnerProfileImageUrl } : {}),
  };
}

export function hasReclaimPartnerCodeIntakeOutcome(
  answers: Record<string, unknown> | null | undefined,
): boolean {
  return !!getReclaimPartnerCodeIntakeState(answers).status;
}

export function isReclaimPartnerCodeIntakeComplete(
  answers: Record<string, unknown> | null | undefined,
): boolean {
  const status = getReclaimPartnerCodeIntakeState(answers).status;
  return (
    status === "applied" ||
    status === "skipped_free_terms" ||
    status === "skipped_without_code"
  );
}

function parseString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

function parseStatus(
  value: unknown,
): ReclaimPartnerCodeIntakeStatus | undefined {
  return typeof value === "string" &&
    (reclaimPartnerCodeIntakeStatuses as readonly string[]).includes(value)
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (value as ReclaimPartnerCodeIntakeStatus)
    : undefined;
}
